@if (isExactMatch) {
  <!-- EXACT MATCH: Two-column Incorrect / Correct cards -->
  @for (criteria of item.criteria; track criteria.id) {
    <div>
      <!-- Header with criteria name and standard -->
      <div class="flex items-center gap-2 flex-wrap mb-2">
        <span class="text-xs font-semibold text-gray-700">{{ criteria.name }}</span>
        @if (criteria.standard) {
          <button
            type="button"
            (click)="onShowStandard(criteria)"
            class="flex items-center gap-1 bg-purple-50 border border-purple-100 rounded px-1.5 py-0.5 cursor-pointer hover:bg-purple-100 transition-colors"
          >
            <lucide-icon [img]="icons.BookOpen" class="w-3 h-3 text-purple-600"></lucide-icon>
            <span class="text-[10px] font-medium text-purple-700">{{ criteria.standard.standardId }}</span>
          </button>
        }
      </div>

      <!-- Two-column level cards -->
      <div class="grid grid-cols-2 gap-2">
        @for (level of criteria.levels; track $index) {
          <div
            class="rounded-lg p-2.5 border bg-white"
            [class.border-red-200]="$index === 0"
            [class.border-green-200]="$index === 1"
          >
            <div class="flex items-center justify-between mb-1.5">
              <span
                class="flex items-center gap-1 text-xs font-semibold"
                [class.text-red-600]="$index === 0"
                [class.text-green-600]="$index === 1"
              >
                <lucide-icon
                  [img]="$index === 0 ? icons.X : icons.Check"
                  class="w-3.5 h-3.5"
                ></lucide-icon>
                {{ $index === 0 ? 'Incorrect' : 'Correct' }}
              </span>
              <span class="text-xs text-gray-400">{{ level.points }} pts</span>
            </div>
            @if (hasNonDefaultDescription(level, $index)) {
              <p class="text-xs text-gray-600 leading-tight">{{ level.description }}</p>
            }
          </div>
        }
      </div>
    </div>
  }
} @else if (isSingleCriteria) {
  <!-- SINGLE CRITERIA (rubric/logic): Horizontal Cards (Option A) -->
  @if (item.criteria[0]; as criteria) {
    <div>
      <!-- Header with criteria name and standard -->
      <div class="flex items-center gap-2 flex-wrap mb-2">
        <span class="text-xs font-semibold text-gray-700">{{ criteria.name }}</span>
        @if (criteria.standard) {
          <button
            type="button"
            (click)="onShowStandard(criteria)"
            class="flex items-center gap-1 bg-purple-50 border border-purple-100 rounded px-1.5 py-0.5 cursor-pointer hover:bg-purple-100 transition-colors"
          >
            <lucide-icon [img]="icons.BookOpen" class="w-3 h-3 text-purple-600"></lucide-icon>
            <span class="text-[10px] font-medium text-purple-700">{{ criteria.standard.standardId }}</span>
          </button>
        }
      </div>

      <!-- Horizontal Level Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
        @for (level of criteria.levels; track $index) {
          <div
            class="rounded-lg p-2.5 border bg-white"
            [class.border-red-200]="$index === 0"
            [class.border-orange-200]="$index === 1"
            [class.border-blue-200]="$index === 2"
            [class.border-green-200]="$index === 3"
          >
            <div class="flex items-center justify-between mb-1.5">
              <span
                class="text-xs sm:text-[10px] font-semibold"
                [class.text-red-600]="$index === 0"
                [class.text-orange-600]="$index === 1"
                [class.text-blue-600]="$index === 2"
                [class.text-green-600]="$index === 3"
              >
                {{ getLevelLabel($index) }}
              </span>
              <span class="text-xs sm:text-[10px] text-gray-400">{{ level.points }} pts</span>
            </div>
            <p class="text-xs sm:text-[10px] text-gray-600 leading-tight">{{ level.description }}</p>
          </div>
        }
      </div>
    </div>
  }
} @else {
  <!-- MULTI-CRITERIA (matrix): Stacked Cards (Option B) with logic-style level cards -->
  <div class="space-y-4">
    @for (criteria of item.criteria; track criteria.id) {
      <div>
        <!-- Criteria Header -->
        <div class="flex items-center gap-2 flex-wrap mb-2">
          <span class="text-xs font-semibold text-gray-700">{{ criteria.name }}</span>
          @if (criteria.standard) {
            <button
              type="button"
              (click)="onShowStandard(criteria)"
              class="flex items-center gap-1 bg-purple-50 border border-purple-100 rounded px-1.5 py-0.5 cursor-pointer hover:bg-purple-100 transition-colors"
            >
              <lucide-icon [img]="icons.BookOpen" class="w-3 h-3 text-purple-600"></lucide-icon>
              <span class="text-[10px] font-medium text-purple-700">{{ criteria.standard.standardId }}</span>
            </button>
          }
        </div>

        <!-- Horizontal Level Cards (same as logic item type) -->
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
          @for (level of criteria.levels; track $index) {
            <div
              class="rounded-lg p-2.5 border bg-white"
              [class.border-red-200]="$index === 0"
              [class.border-orange-200]="$index === 1"
              [class.border-blue-200]="$index === 2"
              [class.border-green-200]="$index === 3"
            >
              <div class="flex items-center justify-between mb-1.5">
                <span
                  class="text-xs sm:text-[10px] font-semibold"
                  [class.text-red-600]="$index === 0"
                  [class.text-orange-600]="$index === 1"
                  [class.text-blue-600]="$index === 2"
                  [class.text-green-600]="$index === 3"
                >
                  {{ getLevelLabel($index) }}
                </span>
                <span class="text-xs sm:text-[10px] text-gray-400">{{ level.points }} pts</span>
              </div>
              <p class="text-xs sm:text-[10px] text-gray-600 leading-tight">{{ level.description }}</p>
            </div>
          }
        </div>
      </div>
    }
  </div>
}

<!-- Standard Details Modal (self-contained) -->
@if (selectedStandard(); as standardData) {
  <app-standard-info-modal
    [data]="standardData"
    (close)="closeStandardModal()"
  />
}
